import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Icon } from '../../../layout/Icon.jsx';
import { useToast } from '../../../shared/useToast.js';
import { useCreateCard, useCreateBulkCards } from '../hooks/useCards.js';

const TABS = [
  { key: 'single', label: 'كارت واحد' },
  { key: 'bulk', label: 'دفعة (مجموعة)' }
];

export function AddCardModal({ open, onClose }) {
  const [tab, setTab] = useState('single');
  const [single, setSingle] = useState({ uid: '', balance: 0, notes: '' });
  const [bulk, setBulk] = useState({ prefix: 'RF', start: 1, end: 50, padLength: 4, balance: 0 });
  const [bulkResult, setBulkResult] = useState(null);

  const toast = useToast((s) => s.add);
  const createOne = useCreateCard();
  const createMany = useCreateBulkCards();

  useEffect(() => {
    if (!open) {
      setTab('single');
      setSingle({ uid: '', balance: 0, notes: '' });
      setBulk({ prefix: 'RF', start: 1, end: 50, padLength: 4, balance: 0 });
      setBulkResult(null);
    }
  }, [open]);

  if (!open) return null;

  async function handleSingle(e) {
    e.preventDefault();
    if (!single.uid.trim()) {
      toast({ type: 'error', message: 'UID مطلوب' });
      return;
    }
    await createOne.mutateAsync(
      { uid: single.uid.trim().toUpperCase(), balance: Number(single.balance) || 0, notes: single.notes },
      { onSuccess: () => onClose() }
    );
  }

  async function handleBulk(e) {
    e.preventDefault();
    setBulkResult(null);
    const out = await createMany.mutateAsync({
      prefix: bulk.prefix,
      start: Number(bulk.start),
      end: Number(bulk.end),
      padLength: Number(bulk.padLength),
      balance: Number(bulk.balance) || 0
    });
    setBulkResult(out);
    toast({
      type: 'success',
      message: `تم إنشاء ${out.inserted} كارت • تخطّي ${out.skipped} موجود مسبقاً`
    });
  }

  const previewSample = (() => {
    const px = String(bulk.prefix || '').toUpperCase();
    const pad = Math.max(1, Number(bulk.padLength) || 4);
    const s = Number(bulk.start) || 0;
    const e = Number(bulk.end) || 0;
    const first = `${px}${String(s).padStart(pad, '0')}`;
    const last = `${px}${String(e).padStart(pad, '0')}`;
    const total = Math.max(0, e - s + 1);
    return { first, last, total };
  })();

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4"
    >
      <div className="bg-white rounded-[14px] shadow-pop w-full max-w-[560px] overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <h3 className="font-extrabold text-[15px] m-0">إضافة بطاقات</h3>
          <button type="button" onClick={onClose} className="text-ink-3 hover:text-ink">
            <Icon name="x" size={18} />
          </button>
        </div>

        <div className="px-5 pt-4">
          <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px]">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={clsx(
                  'px-3.5 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
                  tab === t.key ? 'bg-orange text-white shadow-orange-glow' : 'text-ink-2 hover:bg-white'
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === 'single' ? (
          <form onSubmit={handleSingle} className="p-5 space-y-3">
            <div>
              <label className="block text-[12.5px] font-semibold mb-1">UID الكارت</label>
              <input
                className="input text-[14px] font-mono"
                placeholder="RF0001 أو امسح الكارت بالقارئ"
                value={single.uid}
                onChange={(e) => setSingle({ ...single, uid: e.target.value })}
                autoFocus
              />
              <div className="text-ink-3 text-[10.5px] mt-1">
                يُحوَّل تلقائياً إلى أحرف كبيرة. يقبل القارئ الذي يكتب نهايته Enter.
              </div>
            </div>
            <div>
              <label className="block text-[12.5px] font-semibold mb-1">الرصيد الابتدائي (د.ع)</label>
              <input
                type="number"
                className="input text-[14px]"
                value={single.balance}
                onChange={(e) => setSingle({ ...single, balance: e.target.value })}
                min="0"
              />
            </div>
            <div>
              <label className="block text-[12.5px] font-semibold mb-1">ملاحظات (اختياري)</label>
              <input
                className="input text-[13px]"
                value={single.notes}
                onChange={(e) => setSingle({ ...single, notes: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn" onClick={onClose}>إلغاء</button>
              <button type="submit" className="btn btn-primary" disabled={createOne.isPending}>
                {createOne.isPending ? 'جاري الإضافة…' : 'إضافة'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleBulk} className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12.5px] font-semibold mb-1">البادئة</label>
                <input
                  className="input text-[14px] font-mono"
                  value={bulk.prefix}
                  onChange={(e) => setBulk({ ...bulk, prefix: e.target.value })}
                  placeholder="RF"
                  maxLength={8}
                />
              </div>
              <div>
                <label className="block text-[12.5px] font-semibold mb-1">طول الترقيم</label>
                <input
                  type="number"
                  className="input text-[14px]"
                  value={bulk.padLength}
                  onChange={(e) => setBulk({ ...bulk, padLength: e.target.value })}
                  min="1"
                  max="8"
                />
              </div>
              <div>
                <label className="block text-[12.5px] font-semibold mb-1">من رقم</label>
                <input
                  type="number"
                  className="input text-[14px]"
                  value={bulk.start}
                  onChange={(e) => setBulk({ ...bulk, start: e.target.value })}
                  min="0"
                />
              </div>
              <div>
                <label className="block text-[12.5px] font-semibold mb-1">إلى رقم</label>
                <input
                  type="number"
                  className="input text-[14px]"
                  value={bulk.end}
                  onChange={(e) => setBulk({ ...bulk, end: e.target.value })}
                  min="0"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-[12.5px] font-semibold mb-1">رصيد ابتدائي لكل كارت (د.ع)</label>
                <input
                  type="number"
                  className="input text-[14px]"
                  value={bulk.balance}
                  onChange={(e) => setBulk({ ...bulk, balance: e.target.value })}
                  min="0"
                />
              </div>
            </div>

            <div className="bg-paper-2 rounded-[10px] p-3 text-[12.5px]">
              <div className="font-semibold mb-1">المعاينة</div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-ink-3">أول كارت:</span>
                <span className="font-mono font-bold">{previewSample.first}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-ink-3">آخر كارت:</span>
                <span className="font-mono font-bold">{previewSample.last}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-ink-3">العدد الإجمالي:</span>
                <span className="font-bold text-orange-deep">{previewSample.total} كارت</span>
              </div>
              {previewSample.total > 1000 ? (
                <div className="text-red text-[11.5px] mt-1">⚠️ الحد الأقصى 1000 كارت في العملية الواحدة</div>
              ) : null}
            </div>

            {bulkResult ? (
              <div className="bg-green-50 border border-green-200 rounded-[10px] p-3 text-[12.5px]">
                <div className="font-bold text-green-700 mb-1">✓ اكتملت العملية</div>
                <div>المطلوب: <b>{bulkResult.requested}</b></div>
                <div>تمت الإضافة: <b>{bulkResult.inserted}</b></div>
                <div>تخطّي (موجودة مسبقاً): <b>{bulkResult.skipped}</b></div>
              </div>
            ) : null}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn" onClick={onClose}>
                {bulkResult ? 'إغلاق' : 'إلغاء'}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createMany.isPending || previewSample.total < 1 || previewSample.total > 1000}
              >
                {createMany.isPending ? 'جاري الإضافة…' : `إضافة ${previewSample.total} كارت`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
